/**
 * The membership payment step (ELARA-1012) — step 3 of signup, and the gate a member sees on any
 * later session until the first payment lands.
 *
 * Sits between account creation and onboarding. The account exists by the time anyone gets here, but
 * it is locked: `App` redirects every authenticated route to this page while
 * `MembershipPaymentState.requiresPayment` holds, and releases it the moment payment settles. That
 * is why this page has its own route rather than living inside the signup form — a member who
 * abandons at the card fields and signs back in tomorrow must land here, not on a form that assumes
 * it is creating an account.
 *
 * Card details go from the Stripe Payment Element straight to Stripe. `StartMembershipPayment` opens
 * a card collection (a SetupIntent — no subscription, no charge, nothing to abandon), and the
 * membership itself is created by `CompleteMembershipPayment` once that card exists. Prices on the
 * cards come from `GetMembershipPlans`, which creates nothing, so choosing a plan is free.
 */

import type { MembershipPaymentState } from '@elara/client-core/states/MembershipPaymentState';

import React from 'react';

import { ROUTES } from '@elara/client-core/constants';
import { generatePageTitle } from '@elara/client-core/lib/page-title';

import {
  useMembershipPaymentState,
  useNavigate,
  usePageTitle,
  useQueryParams,
  useSessionState,
} from '#states/hooks.js';
import { Alert, Button } from '#ui/index.js';
import { SessionLayout } from '#views/layout/SessionLayout.jsx';

import { MembershipPaymentForm } from './MembershipPaymentForm.jsx';

/**
 * Query params Stripe appends when it sends a 3DS redirect back to `return_url`. Their presence is
 * what tells this page it is resuming a confirmation rather than starting one.
 */
const STRIPE_REDIRECT_STATUS = 'redirect_status';
/** Stripe also appends the SetupIntent it just confirmed, which is what the membership is made from. */
const STRIPE_SETUP_INTENT = 'setup_intent';

/**
 * The lapsed-membership screen: one button, into Stripe's portal.
 *
 * Says what happened and what will fix it, without guessing which of cancelled / unpaid / paused
 * they are — the portal shows them that, and it is the only place they can act on it. The
 * navigation is a full page load to Stripe, so it happens here rather than in the store.
 */
function LapsedMembership({ state }: { state: MembershipPaymentState }) {
  const [isLeaving, setIsLeaving] = React.useState(false);

  const openPortal = async () => {
    setIsLeaving(true);
    const url = await state.openBillingPortal();
    if (!url) {
      setIsLeaving(false);
      return;
    }
    window.location.href = url;
  };

  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <p className="text-sm">
          Your membership isn’t active at the moment. You can pick it back up in your billing settings — update your
          card, settle anything outstanding, and you’ll be straight back in.
        </p>
        <p className="text-xs text-muted-foreground">
          Billing is handled by Stripe, so your card details never reach Elara.
        </p>
      </div>

      {state.error && <Alert variant="destructive">{state.error}</Alert>}

      <Button
        size="lg"
        className="w-full cursor-pointer"
        disabled={isLeaving || state.isOpeningPortal}
        onClick={() => {
          void openPortal();
        }}
      >
        {isLeaving ? 'Opening billing settings…' : 'Open billing settings'}
      </Button>
    </div>
  );
}

/**
 * The ended-membership screen: a comped membership an operator has cancelled.
 *
 * Deliberately no button. The card form would charge someone who owes nothing, and Stripe's portal
 * cannot revive a cancelled subscription — so an action here would be a promise the system cannot
 * keep. Restoring a comp is a person's decision, and the copy says so.
 */
function EndedMembership() {
  return (
    <div className="space-y-2">
      <p className="text-sm">
        Your membership has ended. If you think that’s a mistake, or you’d like to come back, get in touch and we’ll
        sort it out.
      </p>
      <p className="text-xs text-muted-foreground">Nothing has been charged.</p>
    </div>
  );
}

export function MembershipPaymentPage() {
  const state = useMembershipPaymentState();
  const sessionState = useSessionState();
  const { navigate } = useNavigate();
  const query = useQueryParams();
  const pageTitle = usePageTitle();

  const redirectStatus = query[STRIPE_REDIRECT_STATUS];
  const setupIntentId = query[STRIPE_SETUP_INTENT];
  const [hasStarted, setHasStarted] = React.useState(false);

  // Read in render so the effect below re-runs when the profile listener delivers, rather than
  // deciding once against data that has not arrived yet.
  const requiresPayment = state.requiresPayment;

  // The plan cards are a read — it creates nothing in Stripe — so it runs on mount regardless of
  // whether this member will end up paying, and independently of the effect below.
  React.useEffect(() => {
    void state.loadOfferedPlans();
  }, [state]);

  // A lapsed membership is a different problem from an unpaid one: they HAVE a subscription, so it
  // needs paying or repairing rather than replacing. The card form would mint a second one, and the
  // server refuses it for exactly that reason — so this page shows them the portal instead.
  const hasLapsed = state.hasLapsed;
  // A comped membership that is over has no self-serve remedy at all: nothing to pay, nothing the
  // portal can revive. The screen says to get in touch, and nothing is prepared.
  const hasEnded = state.hasEnded;

  React.useEffect(() => {
    if (hasStarted) return;
    // Nothing to prepare for a lapsed or ended member: neither route is a card collection.
    if (hasLapsed || hasEnded) return;

    // Coming back from a 3DS redirect on the CARD step. Stripe appends the SetupIntent id, which is
    // exactly what the membership needs to be created from — so the return leg is deterministic
    // rather than a guess, and it must not re-prepare (that would open a second collection).
    if (redirectStatus === 'succeeded' && setupIntentId) {
      setHasStarted(true);
      void (async () => {
        if (await state.completePayment(setupIntentId)) navigate(ROUTES.HOME, { replace: true });
      })();
      return;
    }

    // Nothing owed (a comped membership, an already-active one) or the profile has not landed yet.
    // Either way, do not start a payment: `App`'s gate releases the first two off this page, and
    // the third resolves into a real answer a moment later, which re-runs this.
    if (!requiresPayment) return;

    setHasStarted(true);
    void state.preparePayment();
  }, [hasStarted, redirectStatus, setupIntentId, requiresPayment, hasLapsed, hasEnded, state, navigate]);

  // One exit from this page, whichever route reached `active`: a confirmation that settled, the 3DS
  // return leg, or `StartMembershipPayment` finding Stripe already entitles this member. Without
  // this last case a member whose projection had gone stale would sit on a spinner while `App`'s
  // gate had already released them.
  React.useEffect(() => {
    if (state.phase === 'active') navigate(ROUTES.HOME, { replace: true });
  }, [state.phase, navigate]);

  const returnUrl = `${window.location.origin}${ROUTES.MEMBERSHIP_PAYMENT}`;

  const footerContent = (
    <p>
      Not ready yet?{' '}
      <Button
        variant="link"
        className="h-auto p-0 text-sm text-accent"
        onClick={() => {
          void sessionState.logout();
        }}
      >
        Sign out
      </Button>
    </p>
  );

  // "Restart" for anyone who has been a member before: a cancelled member re-subscribing, or a lapsed
  // one being sent to the portal. "Begin" would be wrong to both.
  const heading = hasEnded
    ? 'Your membership has ended'
    : state.isReturning || hasLapsed
      ? 'Restart your membership'
      : 'Begin your membership';

  // The tab title follows the heading. The route config sets a static one on navigation, and a member
  // who is returning would otherwise sit under a tab that says "Begin" while the page says "Restart".
  React.useEffect(() => {
    pageTitle.setTitle(generatePageTitle(heading));
  }, [heading, pageTitle]);

  return (
    <SessionLayout title={heading} footer={footerContent}>
      <div className="space-y-6">
        {/* A failed 3DS leg is a declined card, not a broken account: the form below is re-armed by
            `preparePayment`, so say what happened and let them try again in place. */}
        {redirectStatus === 'failed' && (
          <Alert variant="destructive">
            That card could not be verified. Please try again, or use a different card. Nothing has been charged.
          </Alert>
        )}

        {hasEnded ? (
          <EndedMembership />
        ) : hasLapsed ? (
          <LapsedMembership state={state} />
        ) : (
          <MembershipPaymentForm
            state={state}
            returnUrl={returnUrl}
            onActivated={() => navigate(ROUTES.HOME, { replace: true })}
          />
        )}
      </div>
    </SessionLayout>
  );
}
